/** @jsxImportSource davaux/client */
import { reactive } from 'davaux'
import { createEffect, createSignal } from 'davaux/client'
import type { SliderMark } from './Slider.js'

export type { SliderMark }

/** Props for the RangeSlider component. */
export interface RangeSliderProps {
  /** Initial range as `[start, end]` (SSR snapshot and client initial state). */
  value?: [number, number]
  /** Minimum selectable value; defaults to `0`. */
  min?: number
  /** Maximum selectable value; defaults to `100`. */
  max?: number
  /** Step between selectable values; defaults to `1`. */
  step?: number
  /** Tick marks shown along the track. */
  marks?: SliderMark[]
  /** Accent color for the filled range and thumbs. */
  color?: string
  /** Controls track height and thumb size via design-token size steps. */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  /** Border-radius of the thumbs. */
  radius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | string | number
  /** When true, disables interaction. */
  disabled?: boolean
  /** Name prefix for the two hidden inputs; produces `{name}Low` and `{name}High`. */
  name?: string
  /** Called with the new range whenever either thumb is dragged. */
  onChange?: (value: [number, number]) => void
  /** Additional CSS class names applied to the root element. */
  className?: string
  [key: string]: unknown
}

function RangeSlider({
  value: initialValue,
  min = 0,
  max = 100,
  step = 1,
  marks,
  color,
  size,
  radius: r,
  disabled,
  name,
  onChange,
  class: className,
}: RangeSliderProps) {
  const [low, setLow] = createSignal(initialValue?.[0] ?? min)
  const [high, setHigh] = createSignal(initialValue?.[1] ?? max)
  const [lowOnTop, setLowOnTop] = createSignal(true)

  const lowRef = { current: null as HTMLInputElement | null }
  const highRef = { current: null as HTMLInputElement | null }

  createEffect(() => {
    if (lowRef.current) lowRef.current.value = String(low())
  })
  createEffect(() => {
    if (highRef.current) highRef.current.value = String(high())
  })

  const span = max > min ? max - min : 1
  const pctLow = () => ((low() - min) / span) * 100
  const pctHigh = () => ((high() - min) / span) * 100

  function handlePointerMove(e: PointerEvent) {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const pct = ((e.clientX - rect.left) / rect.width) * 100
    setLowOnTop(Math.abs(pct - pctLow()) <= Math.abs(pct - pctHigh()))
  }

  const rootStyle = () => {
    const vars: Record<string, string> = {
      '--dv-range-slider-fill-start': `${pctLow()}%`,
      '--dv-range-slider-fill-end': `${pctHigh()}%`,
    }
    if (color !== undefined) vars['--dv-slider-color'] = `var(--dv-color-${color}-filled)`
    if (size !== undefined) vars['--dv-slider-size'] = `var(--dv-slider-size-${size})`
    if (r !== undefined) {
      vars['--dv-slider-radius'] = typeof r === 'number' ? `${r}px` : `var(--dv-radius-${r}, ${r})`
    }
    return vars
  }

  function handleLowInput(e: Event) {
    const next = Math.min(Number((e.target as HTMLInputElement).value), high() - step)
    setLow(next)
    onChange?.([next, high()])
  }

  function handleHighInput(e: Event) {
    const next = Math.max(Number((e.target as HTMLInputElement).value), low() + step)
    setHigh(next)
    onChange?.([low(), next])
  }

  return (
    <div
      class={`dv-range-slider${className ? ` ${className}` : ''}`}
      style={rootStyle}
      data-disabled={disabled || undefined}
    >
      <div class='dv-range-slider-track'>
        <input
          class='dv-range-slider-thumb-start'
          type='range'
          min={min}
          max={max}
          step={step}
          value={String(low())}
          name={name ? `${name}Low` : undefined}
          disabled={disabled || undefined}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={() => low()}
          aria-label='Range start'
          style={() => ({ 'z-index': lowOnTop() ? '3' : '2' })}
          ref={lowRef}
          onPointerMove={handlePointerMove}
          onInput={handleLowInput}
        />
        <input
          class='dv-range-slider-thumb-end'
          type='range'
          min={min}
          max={max}
          step={step}
          value={String(high())}
          name={name ? `${name}High` : undefined}
          disabled={disabled || undefined}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={() => high()}
          aria-label='Range end'
          style={() => ({ 'z-index': lowOnTop() ? '2' : '3' })}
          ref={highRef}
          onPointerMove={handlePointerMove}
          onInput={handleHighInput}
        />
      </div>
      {marks && marks.length > 0 && (
        <div class='dv-slider-marks'>
          {marks.map((m) => {
            const pctMark = ((m.value - min) / span) * 100
            return (
              <>
                <div
                  class='dv-slider-mark'
                  style={{ '--dv-slider-mark-offset': `${pctMark}%` }}
                  data-filled={() => (m.value >= low() && m.value <= high()) || undefined}
                />
                {m.label !== undefined && (
                  <div
                    class='dv-slider-mark-label'
                    style={{ '--dv-slider-mark-offset': `${pctMark}%` }}
                  >
                    {m.label}
                  </div>
                )}
              </>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default reactive(RangeSlider)
